// ADR-104: a card's value for a field type is its first non-blank field of that
// type, otherwise none. This is the one client-side statement of that rule
// (Decision 2). The backend twin is `value_side` / `has_value_sql` in
// `backend/app/models/card_side.py`, with the same blank test, so a side cannot
// be filled on one side of the wire and blank on the other.

/** The field types ADR-104 decides. Other kinds are not governed by it. */
export type ValueKind = 'translation' | 'definition' | 'example'

/** Decision 1's blank test: nothing left after trimming space, tab, CR, LF.
 * Deliberately not `String.trim()`, which also strips other Unicode
 * whitespace and would disagree with the server's `btrim(content, ' \t\r\n')`. */
const BLANK_RE = /^[ \t\r\n]*$/

export function isBlank(content: string | null | undefined): boolean {
  return BLANK_RE.test(content ?? '')
}

interface ValueCandidate {
  kind?: string | null
  content: string
  position?: number
}

/**
 * The side holding `kind`'s value, or `undefined` when the card has none.
 *
 * The first side of `kind` in stored order whose content is not blank. Position
 * still decides between two non-blank sides (ADR-066 Decision 1); a blank side
 * holds no value and does not hide a later one.
 *
 * Matches on `kind` (ADR-040), never on the cosmetic label. When every side
 * carries a numeric `position` the sides are read in position order; otherwise
 * (Learn and Classic payloads, editor drafts) the array's own order is taken
 * as the stored order, which is the order every caller holds them in.
 *
 * Returns the side itself: callers need its id, form or translations, and must
 * never look the side up again by its text.
 */
export function valueSide<T extends ValueCandidate>(
  sides: readonly T[], kind: ValueKind,
): T | undefined {
  const ordered = sides.every((s) => typeof s.position === 'number')
    ? [...sides].sort((a, b) => (a.position as number) - (b.position as number))
    : sides
  return ordered.find((s) => s.kind === kind && !isBlank(s.content))
}

/** ADR-104 `filled`: the card has a value for `kind`. */
export function hasValue(sides: readonly ValueCandidate[], kind: ValueKind): boolean {
  return valueSide(sides, kind) !== undefined
}
